import { useState, useEffect } from 'react'
import Modal from '@components/modal/Modal'
import PriceTable, { TableContext } from '@components/payment/PriceTable'
import PaymentButton from '@components/payment/PaymentButton'

import useSWR from 'swr'
import { apiFetcherPost as apiFetcher, post } from '@api/apiFetcher'
import useRedirect from '@hooks/useRedirectBack'
import useT from '@hooks/useTranslation'

const CHECK_BIG_WINDOW = '/api/highlights/windows/big/available'
const CHECK_SMALL_WINDOW = '/api/highlights/windows/small/available'
const RESERVE_WINDOWS = '/api/highlights/windows/reserve'
const UNDO_RESERVATIONS = '/api/highlights/windows/undo_reservation'

const API = '/api/payment/highlight'
const GET_PRICE_TABLES = '/api/highlights/prices'
const PING_SETTING = { refreshInterval: 15 * 60 * 1000 }

const Highlight = ({
    user,
    highlight: {
        _id = '',
        type = '',
        promotedInSmallWindow = false,
        promotedInBigWindow = false,
        promotedOnLists = false,
    } = {},
    shouldDisplay,
    onCancel }) => {

    const [selected, setSelected] = useState({})
    const [orderCode, setOrderCode] = useState('')
    const [price, setPrice] = useState(0)
    const [done, setDone] = useState(false)
    const [redirect] = useRedirect()

    const windowPromoAllowed = ['car_ad', 'truck_ad', 'motorcycle_ad'].includes(type)

    const { data: availableSmallWindow } = useSWR(shouldDisplay && CHECK_SMALL_WINDOW, apiFetcher, PING_SETTING)
    const { data: availableBigWindow } = useSWR(shouldDisplay && CHECK_BIG_WINDOW, apiFetcher, PING_SETTING)
    const { data: priceTables } = useSWR(shouldDisplay && GET_PRICE_TABLES, apiFetcher, PING_SETTING)
    const isReady = shouldDisplay
        && availableBigWindow != null
        && availableBigWindow != null
        && priceTables != null

    useEffect(() => {
        if (shouldDisplay) {
            post({ path: RESERVE_WINDOWS, auth: user.token }).then(res => null)
            return () => post({ path: UNDO_RESERVATIONS, auth: user.token }).then(res => null)
        }
    }, [shouldDisplay])

    useEffect(() => {
        let priceNew = 0
        const orderCodeParts = [`promote?${type.replace('_', '-')}`]
        for (const row in priceTables) {
            for (const item of priceTables[row]) {
                if (item._id in selected) {
                    priceNew += item.price
                    orderCodeParts.push(`${row}:${item.duration}`)
                }
            }
        }

        setOrderCode(priceNew !== 0
            ? `${orderCodeParts.join('&')}_${_id}`
            : '')

        setPrice(priceNew)
    }, [selected])

    const close = () => setDone(true)
    const setDoneAndRedirect = () => {
        setDone(true)
        redirect()
    }

    const contextData = {
        isReady,
        table: {
            columnHeads: [
                'Miejsce wyświetlenia',
                '1 dzień',
                '3 dni',
                '5 dni',
                '7 dni'
            ],
            rowHeads: [
                'Wyświetlanie ogłoszenia na szczycie w wynikach wyszukiwania',
                'Rotacyjne wyświetlanie w małym oknie w sekcji ofert wyróżnionych na stronie głównej',
                'Wyświetlanie w wielkim oknie w sekcji ofert wyróżnionych na stronie głównej',
            ],
            rowsAvailable: [
                !promotedOnLists && true,
                !promotedInSmallWindow && windowPromoAllowed && availableSmallWindow,
                !promotedInBigWindow && windowPromoAllowed && availableBigWindow,
            ],
            clear: () => setSelected({}),
            items: priceTables,
            itemsSelected: selected,
            itemDisplayRule: (item) => `${item.price} zł`,
            itemClick: (row, item) => {
                const { _id } = item
                const selectedNew = {...selected}
                if (_id in selectedNew) {
                    delete selectedNew[_id]

                } else {
                    priceTables[row].forEach(item => {
                        delete selectedNew[item._id]
                    })
                    selectedNew[_id] = true
                }

                setSelected(selectedNew)
            },
        }
    }

    return (
        <Modal open={isReady && !done} onClose={setDoneAndRedirect} locked>
            <TableContext.Provider value={contextData}>
                <div style={{ maxWidth: 800 }}>
                    <h1>{useT('Wyróżnij ogłoszenie')}</h1>
                    <p>{useT('Dostępne metody promocji możemy podzielić na ekskluzywne i ogólnodostępne. Promocje ekskluzywne obejmują wyświetlenia w wielkim lub w małym oknie na stronie głównej. Ponieważ ich dostępność jest ściśle ograniczona, gwarantują pewną wyłączność i najwyższą ilość wyświetleń. Ogólnie dostępną strategią jest możliwość umieszczenia ogłoszenia na szczycie listy wyników wyszukiwania. Możesz łączyć różne metody promocji, aby uzyskać lepsze efekty')}.</p>
                    <PriceTable />
                    <PaymentButton
                        user={user}
                        price={price}
                        apiPath={API}
                        orderCode={orderCode}
                        onSuccess={setDoneAndRedirect}
                        onClose={close}
                        onCancel={onCancel || setDoneAndRedirect} />
                </div>
            </TableContext.Provider>
        </Modal>
    )
}

export default Highlight